import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { apiFetch } from '@/lib/apiClient';
import { getAuthItem } from '@/lib/authStorage';
import { useModalOverlayClick } from '@/common/hooks/useModalOverlayClick';
import './MobileSettingsModal.css';

interface MobileSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function MobileSettingsModal({ isOpen, onClose }: MobileSettingsModalProps) {
  const [modalContentRef, handleOverlayClick, handleOverlayMouseDown] = useModalOverlayClick(onClose, 30);
  const [notificationTier, setNotificationTier] = useState<string>('normal');
  const [isLoadingTier, setIsLoadingTier] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const authToken = getAuthItem('xavior_auth_token') ?? undefined;
    if (!authToken) return;
    apiFetch('/auth/session', { authToken })
      .then((data: any) => {
        if (data?.notificationTier) {
          setNotificationTier(data.notificationTier);
        }
      })
      .catch(() => {});
  }, [isOpen]);

  const handleTierChange = async (tier: string) => {
    if (isLoadingTier || tier === notificationTier) return;
    setIsLoadingTier(true);
    const authToken = getAuthItem('xavior_auth_token') ?? undefined;
    try {
      await apiFetch('/auth/notification-tier', {
        method: 'PATCH',
        body: JSON.stringify({ tier }),
        authToken,
      });
      setNotificationTier(tier);
    } catch (err) {
      console.error('Failed to update notification tier:', err);
    } finally {
      setIsLoadingTier(false);
    }
  };

  if (!isOpen) return null;

  const portalRoot = document.getElementById('modal-portal-root');

  const content = (
    <div className="mobile-settings-overlay" onMouseDown={handleOverlayMouseDown}
 onClick={handleOverlayClick}>
      <div ref={modalContentRef} className="mobile-settings-modal" onClick={(e) => e.stopPropagation()}>
        <div className="mobile-settings-header">
          <h2>Settings</h2>
          <button className="mobile-settings-close" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="mobile-settings-content">
          <div className="mobile-settings-section">
            <h3 className="mobile-settings-section-title">Notifications</h3>

            <div className="mobile-settings-info">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 16v-4M12 8h.01" />
              </svg>
              <span>Push notifications are managed in the XAVIOR mobile app</span>
            </div>

            <div className="mobile-settings-tier-selector" style={{ marginTop: 16 }}>
              <span className="mobile-settings-row-label" style={{ marginBottom: 10, display: 'block' }}>
                Notification Level
              </span>
              <div className="mobile-settings-segmented-control">
                {[
                  { value: 'quiet', label: 'Quiet', desc: 'Daily briefing only' },
                  { value: 'normal', label: 'Normal', desc: 'Briefings + alerts' },
                  { value: 'everything', label: 'Everything', desc: 'All notifications' },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    className={`mobile-settings-segment ${notificationTier === opt.value ? 'active' : ''}`}
                    onClick={() => handleTierChange(opt.value)}
                    disabled={isLoadingTier}
                  >
                    <span className="mobile-settings-segment-label">{opt.label}</span>
                    <span className="mobile-settings-segment-desc">{opt.desc}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );

  return portalRoot ? createPortal(content, portalRoot) : content;
}
